import type { BillingInterval, PlanDefinition } from '@tandemone/types'

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
})

/* every billing date is a stripe timestamp, and stripe anchors the cycle at
 * midnight UTC. rendering that in local time shows the day before for anyone
 * west of UTC, which is how a plan anchored to the 1st read "renews Sep 30". */
export function formatBillingDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function formatPlanPrice(
  plan: PlanDefinition,
  interval: BillingInterval
): string {
  const cents =
    interval === 'year' ? plan.yearlyPriceCents : plan.monthlyPriceCents
  if (cents === 0) return 'Free'
  return `${usd.format(cents / 100)}${interval === 'year' ? ' / yr' : ' / mo'}`
}

/* the plan header sets the amount and its cadence at different sizes, which one
 * string cannot do. a comp, or a supplier its rep covers, is on a paid plan but
 * pays nothing, so its list price would be a lie. */
export function planPriceParts(input: {
  plan: PlanDefinition
  interval: BillingInterval | null
  comped: boolean
  coveredByRep?: boolean
}): { amount: string; per: string } {
  if (input.coveredByRep) return { amount: usd.format(0), per: 'covered' }
  if (input.comped || input.plan.monthlyPriceCents === 0) {
    return {
      amount: usd.format(0),
      per: input.comped ? 'comped' : 'forever'
    }
  }
  const cents =
    input.interval === 'year'
      ? input.plan.yearlyPriceCents
      : input.plan.monthlyPriceCents
  return {
    amount: usd.format(cents / 100),
    per: input.interval === 'year' ? '/ yr' : '/ mo'
  }
}

/* a prorated amount is rarely round, so unlike the catalog prices above this
 * keeps the cents. */
export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(cents / 100)
}

/* derived rather than stated so the catalog stays the source of truth. */
export function yearlySavingMonths(plan: PlanDefinition): number {
  if (plan.monthlyPriceCents === 0) return 0
  const monthsPaid = plan.yearlyPriceCents / plan.monthlyPriceCents
  return Math.round(12 - monthsPaid)
}

export function formatSeats(seatLimit: number | null): string {
  return seatLimit === null ? 'Unlimited' : `${seatLimit}`
}

export function intervalLabel(interval: BillingInterval | null): string {
  if (interval === 'year') return 'Yearly'
  if (interval === 'month') return 'Monthly'
  return ''
}
